import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, Shield, AlertTriangle, RefreshCw, ExternalLink,
  Database, Target, Crosshair, Globe
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ThreatIntelPage() {
  const [feedStatus, setFeedStatus] = useState({});
  const [latestThreats, setLatestThreats] = useState({});
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mitreTactics, setMitreTactics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statusRes, latestRes, tacticsRes] = await Promise.all([
        axios.get(`${API}/api/threat-intel/feeds/status`, { headers }),
        axios.get(`${API}/api/threat-intel/latest?limit=5`, { headers }),
        axios.get(`${API}/api/threat-intel/mitre/tactics`, { headers })
      ]);

      setFeedStatus(statusRes.data);
      setLatestThreats(latestRes.data);
      setMitreTactics(tacticsRes.data);
    } catch (error) {
      toast.error('Failed to load threat intelligence data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.get(`${API}/api/threat-intel/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('grc_token');
      await axios.post(`${API}/api/threat-intel/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feeds refreshed');
      fetchData();
    } catch (error) {
      toast.error('Failed to refresh feeds');
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return <Badge className={colors[severity?.toLowerCase()] || colors.medium}>{severity || 'Unknown'}</Badge>;
  };

  const getFeedStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    if (status === 'configured') return <Badge className="bg-blue-100 text-blue-800">Configured</Badge>;
    return <Badge className="bg-gray-100 text-gray-800">Not Configured</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="threat-intel-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Threat Intelligence</h1>
          <p className="text-muted-foreground">Monitor CVEs, MITRE ATT&CK tactics, and threat feeds</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" data-testid="refresh-feeds-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Feeds
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search CVEs, techniques, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="threat-search-input"
              />
            </div>
            <Button onClick={handleSearch} disabled={searching} data-testid="search-btn">
              {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults && (
        <Card data-testid="search-results">
          <CardHeader>
            <CardTitle>Search Results</CardTitle>
            <CardDescription>
              Found {(searchResults.nvd?.length || 0) + (searchResults.mitre_attack?.length || 0)} results
            </CardDescription>
          </CardHeader>
          <CardContent>
            {searchResults.nvd?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4" /> NVD CVEs
                </h4>
                <div className="space-y-2">
                  {searchResults.nvd.slice(0, 5).map((cve, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-medium">{cve.id}</span>
                        {getSeverityBadge(cve.severity)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {cve.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {searchResults.mitre_attack?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Crosshair className="h-4 w-4" /> MITRE ATT&CK
                </h4>
                <div className="space-y-2">
                  {searchResults.mitre_attack.slice(0, 5).map((technique, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <span className="font-medium">{technique.name}</span>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {technique.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Feed Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="nvd-status">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">NVD (NIST)</CardTitle>
              {getFeedStatusBadge(feedStatus.nvd?.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Database className="h-6 w-6 text-blue-500" />
              CVE Database
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {feedStatus.nvd?.requires_api_key ? 'API Key Recommended' : 'No API Key Required'}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="mitre-status">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">MITRE ATT&CK</CardTitle>
              {getFeedStatusBadge(feedStatus.mitre_attack?.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Target className="h-6 w-6 text-red-500" />
              {mitreTactics.length} Tactics
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Adversary tactics & techniques
            </p>
          </CardContent>
        </Card>

        <Card data-testid="otx-status">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">AlienVault OTX</CardTitle>
              {getFeedStatusBadge(feedStatus.alienvault_otx?.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Globe className="h-6 w-6 text-purple-500" />
              Threat Pulses
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {feedStatus.alienvault_otx?.requires_api_key ? 'API Key Required' : 'Active'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="cves" className="space-y-4">
        <TabsList>
          <TabsTrigger value="cves" data-testid="cves-tab">Latest CVEs</TabsTrigger>
          <TabsTrigger value="mitre" data-testid="mitre-tab">MITRE Tactics</TabsTrigger>
        </TabsList>

        <TabsContent value="cves">
          <Card>
            <CardHeader>
              <CardTitle>Recent CVEs</CardTitle>
              <CardDescription>Latest vulnerabilities from NVD</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {latestThreats.nvd?.map((cve, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`cve-${idx}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        <span className="font-mono font-semibold">{cve.id}</span>
                        {getSeverityBadge(cve.severity)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {cve.description}
                      </p>
                      {cve.published && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Published: {new Date(cve.published).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <a
                        href={`https://nvd.nist.gov/vuln/detail/${cve.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                ))}
                {(!latestThreats.nvd || latestThreats.nvd.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">
                    No recent CVEs available
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mitre">
          <Card>
            <CardHeader>
              <CardTitle>MITRE ATT&CK Tactics</CardTitle>
              <CardDescription>Enterprise adversary tactics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mitreTactics.map((tactic, idx) => (
                  <div
                    key={idx}
                    className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`tactic-${idx}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="h-5 w-5 text-red-500" />
                      <span className="font-semibold">{tactic.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {tactic.description}
                    </p>
                    {tactic.external_id && (
                      <Badge variant="outline" className="mt-2">{tactic.external_id}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
